import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Building2, FileText, Clock, CheckCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * Pagină SEO location-based pentru extras carte funciară per oraș
 * URL: /extras-carte-funciara/bucuresti, /extras-carte-funciara/cluj-napoca, etc.
 */

function LocationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [city, setCity] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Încarcă date oraș + conținut generat
  useEffect(() => {
    const loadCityData = async () => {
      try {
        // 1. Încarcă date orașe din JSON static
        const locationsResponse = await fetch('/seo-data/locations-data.json');
        const locationsData = await locationsResponse.json();

        const cityData = locationsData.cities.find(c => c.slug === slug);

        if (!cityData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setCity(cityData);

        // 2. Încarcă conținut Markdown generat
        const contentResponse = await fetch(`/seo-data/content/${slug}.md`);
        if (!contentResponse.ok) {
          throw new Error('Conținut nu există');
        }

        const markdown = await contentResponse.text();

        // Extrage conținut fără frontmatter
        const contentWithoutFrontmatter = markdown.replace(/^---[\s\S]*?---\n/, '');

        setContent(contentWithoutFrontmatter);
        setLoading(false);

      } catch (error) {
        console.error('Error loading city data:', error);
        setNotFound(true);
        setLoading(false);
      }
    };

    loadCityData();
  }, [slug]);

  // SEO meta tags dinamice (ANTI-CANIBALIZARE)
  useEffect(() => {
    if (city) {
      // URL canonic CU "/" final = exact varianta servită de nginx (fără redirect)
      const pageUrl = `https://cf-extras.ro/extras-carte-funciara/${slug}/`;

      // Title: keyword + oraș la început, sub 60 caractere (fallback scurt pentru orașe cu nume lungi)
      const fullTitle = `Extras Carte Funciară ${city.name} – Online în 5 Minute`;
      document.title = fullTitle.length <= 60 ? fullTitle : `Extras CF ${city.name} – Online în 5 Minute`;

      // Description: mini-anunț, sub ~155 caractere
      const metaDescription = `Extras carte funciară ${city.name} online: document oficial pe email în 5 minute, fără deplasare. Acceptat la notar și bancă, plată cu cardul.`;

      // Helper pentru meta tags
      const updateMetaTag = (attribute, name, content) => {
        let meta = document.querySelector(`meta[${attribute}="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attribute, name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      // 1. Meta description
      updateMetaTag('name', 'description', metaDescription);

      // 2. Canonical URL (CRITIC pentru SEO - să pointeze la propria pagină, nu homepage!)
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = pageUrl;

      // 3. Open Graph tags (pentru share pe social media)
      updateMetaTag('property', 'og:title', document.title);
      updateMetaTag('property', 'og:description', metaDescription);
      updateMetaTag('property', 'og:url', pageUrl);
      updateMetaTag('property', 'og:type', 'website');

      // 4. Twitter Card tags
      updateMetaTag('name', 'twitter:title', document.title);
      updateMetaTag('name', 'twitter:description', metaDescription);

      // 5. BreadcrumbList Schema (JSON-LD)
      let breadcrumbScript = document.querySelector('script[data-schema="breadcrumb"]');
      if (!breadcrumbScript) {
        breadcrumbScript = document.createElement('script');
        breadcrumbScript.type = 'application/ld+json';
        breadcrumbScript.setAttribute('data-schema', 'breadcrumb');
        document.head.appendChild(breadcrumbScript);
      }
      breadcrumbScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Acasă",
            "item": "https://cf-extras.ro/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Extras CF pe orașe",
            "item": "https://cf-extras.ro/extras-carte-funciara/"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `Extras Carte Funciară ${city.name}`,
            "item": pageUrl
          }
        ]
      });
    }

    return () => {
      const script = document.querySelector('script[data-schema="breadcrumb"]');
      if (script) script.remove();
    };
  }, [city, slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Orașul nu a fost găsit
          </h1>
          <p className="text-gray-600 mb-8">
            Pagina pentru acest oraș nu există încă.
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Înapoi la comandă
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50">
      <Header />

      {/* Hero Section cu breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="text-sm mb-4">
            <ol className="flex items-center space-x-2 text-gray-600">
              <li>
                <Link to="/" className="hover:text-emerald-600 transition">
                  Acasă
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/extras-carte-funciara/" className="hover:text-emerald-600 transition">
                  Orașe
                </Link>
              </li>
              <li>/</li>
              <li className="text-gray-900 font-medium">{city.name}</li>
            </ol>
          </nav>

          {/* Title cu icon */}
          <div className="flex items-center space-x-3 mb-4">
            <MapPin className="h-8 w-8 text-emerald-600" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Extras Carte Funciară {city.name}
            </h1>
          </div>

          {/* Subtitle cu date oraș */}
          <p className="text-lg text-gray-600 flex items-center flex-wrap gap-4">
            <span className="flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-emerald-600" />
              {city.stats.companies}
            </span>
            <span className="text-gray-400">•</span>
            <span>{city.population} de locuitori</span>
            <span className="text-gray-400">•</span>
            <span>{city.county}</span>
          </p>
        </div>
      </div>

      {/* Main Content - Layout 2 coloane */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-[1fr_350px] gap-8">
          {/* Coloană stângă - Conținut SEO */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* CTA top - înainte de articol */}
            <div className="mb-8 p-6 bg-emerald-50 rounded-xl border border-emerald-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="h-6 w-6 text-emerald-600 mr-2" />
                Comandă acum extrasul tău
              </h3>
              <p className="text-gray-700 mb-4">
                Procesul durează doar câteva minute. Livrare pe email, fără deplasare.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full sm:w-auto px-8 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition shadow-lg"
              >
                Comandă extras carte funciară
              </button>
            </div>

            {/* Conținut generat GPT-4 */}
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed">
                <ReactMarkdown
                  components={{
                    h2: ({node, children, ...props}) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4" style={{textTransform: 'capitalize'}} {...props}>{children}</h2>,
                    p: ({node, ...props}) => <p className="mb-4" {...props} />
                  }}
                >
                  {city ? (() => {
                    const keywords = [
                      `extras carte funciară ${city.name}`,
                      `extrasul de carte funciară ${city.name}`,
                      `extras carte funciară pentru ${city.name}`,
                      `extras CF ${city.name}`
                    ];
                    let processed = content;
                    // Bold keywords
                    keywords.forEach(keyword => {
                      const regex = new RegExp(`(?<!\\*\\*)${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?!\\*\\*)`, 'gi');
                      processed = processed.replace(regex, `**${keyword}**`);
                    });
                    return processed;
                  })() : content}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          {/* Coloană dreapta - Sidebar cu features */}
          <div className="space-y-6">
            {/* Card avantaje */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                De ce să comanzi online?
              </h3>
              <ul className="space-y-3">
                {[
                  { icon: Clock, text: 'Procesare în 5 minute' },
                  { icon: FileText, text: 'Document oficial valabil' },
                  { icon: MapPin, text: `Fără deplasare în ${city.name}` },
                  { icon: CheckCircle, text: 'Livrare gratuită prin email' }
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <item.icon className="h-5 w-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Card industrii locale */}
            <div className="bg-gradient-to-br from-emerald-600 to-cyan-600 rounded-2xl shadow-lg p-6 text-white">
              <h3 className="text-xl font-bold mb-4">
                Tipuri de proprietăți
              </h3>
              <div className="flex flex-wrap gap-2">
                {city.industries.map((industry, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium"
                  >
                    {industry}
                  </span>
                ))}
              </div>
            </div>

            {/* Card cartiere */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Zone acoperite
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Serviciu disponibil pentru toate cartierele din {city.name}:
              </p>
              <div className="flex flex-wrap gap-2">
                {city.neighborhoods.slice(0, 6).map((neighborhood, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-200"
                  >
                    {neighborhood}
                  </span>
                ))}
                {city.neighborhoods.length > 6 && (
                  <span className="px-3 py-1 text-gray-500 text-sm">
                    +{city.neighborhoods.length - 6} zone
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default LocationPage;
